import { Map } from "immutable";
import _forEach from "lodash/forEach";
import _cloneDeep from "lodash/cloneDeep";
import { formatDistanceToNow } from "date-fns";
import { parseTimestamp } from "utils/helpers/timestampHelper";

/**
 * Calculates if a kiosk is online based on its lastSeenTS timestamp.
 * A kiosk is considered online if lastSeenTS is within the last 15 seconds.
 * @param {string|Date} lastSeenTS - The last seen timestamp (UTC string or Date object)
 * @param {number} thresholdMs - The threshold in milliseconds (default: 15000)
 * @returns {boolean} True if the kiosk is online, false otherwise
 */
export const calculateIsOnline = (lastSeenTS, thresholdMs = 15000) => {
  if (!lastSeenTS) return false;
  
  const lastSeenDate = parseTimestamp(lastSeenTS);
  if (!lastSeenDate) return false;
  
  const now = new Date();
  const diff = now.getTime() - lastSeenDate.getTime();
  const isOnline = diff < thresholdMs;
  return isOnline;
};

export const kiosksBrowserMapStateToProps = (state, ownProps) => {
  const sidebarMode = `${state.getIn(["base", "mainSidebar", "mode"])}Extra`;

  const installationsData = state.getIn(["data", "installations"]).toJS();
  const kiosks = {};

  if (Object.keys(installationsData).length > 0) {
    let counter = 0;
    _forEach(installationsData, (installation, key) => {
      if (key !== "revertCopy") {
        // Calculate isOnline using centralized function
        const isOnline = calculateIsOnline(installation.lastSeenTS);
        installation.isOnline = isOnline;
        kiosks[counter] = {
          id: installation._id,
          columns: {
            1: {
              id: 1,
              label: installation.title || "Untitled Kiosk"
            },
            2: (() => {
              let lastSeenDateStr = "Online";
              if (!installation.isOnline) {
                const lastSeenDate = installation.lastSeenTS ? parseTimestamp(installation.lastSeenTS) : null;
                if (lastSeenDate) {
                  lastSeenDateStr = "Offline (for " + formatDistanceToNow(lastSeenDate) + ")";
                } else {
                  lastSeenDateStr = "Offline";
                }
              }
              return {
                id: 2,
                label: installation.isOnline
                  ? `<div id="indicator_online" class="status-indicator online"><div class="icon"></div><div class="label">${lastSeenDateStr}</div></div>`
                  : `<div id="indicator_offline" class="status-indicator offline"><div class="icon"></div><div class="label">${lastSeenDateStr}</div></div>`
              };
            })(),
            3: (() => {
              const lastCompletedPublishDate = installation.lastCompletedPublishTS
                ? parseTimestamp(installation.lastCompletedPublishTS)
                : null;
              return {
                id: 3,
                label: lastCompletedPublishDate
                  ? formatDistanceToNow(lastCompletedPublishDate, { addSuffix: true })
                  : "Never",
                sortValue: lastCompletedPublishDate ? lastCompletedPublishDate.getTime() : 0
              };
            })()
          }
        };
        counter++;
      }
    });
  }

  return {
    slugBase: "/kiosks",
    kiosks,
    sidebarMode,
    sidebarProps: {
      title: "",
      goBackAction: ""
    }
  };
};

/**
 * installationManagerMapStateToProps
 * @param {object} state
 * @param {object} ownProps
 */
export const installationManagerMapStateToProps = (state, ownProps) => {
  const { params } = ownProps.match;
  const { installationID, section } = params;

  const userPreferencesSelector = [
    "installationManager",
    "editor",
    installationID
  ];
  const userPreferences = state
    .getIn(["ui", "userPreferences", ...userPreferencesSelector], Map({}))
    .toJS();

  const user = state.getIn(["data", "user"], Map);
  const installationTypes = state
    .getIn(["base", "installationTypes"], Map({}))
    .toJS();
  const allProjects = state.getIn(["data", "projects"], Map([])).toJS();
  const installations = state.getIn(["data", "installations"], Map({})).toJS();
  let installationRevertCopy = state.getIn([
    "data",
    "installations",
    "revertCopy"
  ]);
  if (typeof installationRevertCopy !== "undefined") {
    installationRevertCopy = Map(installationRevertCopy).toJS();
  }
  const installationManager = state
    .getIn(["ui", "installationManager"], Map({}))
    .toJS();
  const { isLoading } = installationManager;

  const installationData = installations ? installations[installationID] : null;

  // Calculate isOnline for the installation
  if (installationData) {
    installationData.isOnline = calculateIsOnline(installationData.lastSeenTS);
  }

  // const installationType = selectors.getInstallationTypeParam(
  //   state,
  //   installationData.typeID
  // );
  const installationType = "standard";
  const installationTypeData = installationType
    ? installationTypes[installationType]
    : null;

  /*
  navigation - Elements passed to List component to render a form in the 
    side bar. If the section doesn't have any side bar elements or it has
    custom elements, set navigation to an empty object.
  */
  let navigation = installationTypeData
    ? installationTypeData.navigation
    : null;
  let goBackAction = "/kiosks";
  let sideBarSlugBase = `${goBackAction}/${installationID}`;
  /*
  sidebarData - Data used in sidebar form if one is used. See notes in the
    StoryEditor Sidebar component's componentWillReceiveProps() method for
    more details (/shared/modules/StoryEditor/Sidebar/index.js)
  */
  let sidebarData = _cloneDeep(installationData);
  let sideBarTitle = installationData ? installationData.title : "";
  let structure = null;
  let isFormDataDirty = false; // 7/10/2021 - Need this for main menu, so not managing it for any other data for now.

  if (section) {
    sideBarTitle = section !== "mainmenu" ? navigation[section].label : "Main Menu";
    sideBarSlugBase += `/${section}`;
    goBackAction += `/${installationID}`;

    if (section === "systemReport") {
      navigation = installationManager.forms.systemReport;
      sidebarData = { ...installationData };
    } else if (section === "settings") {
      navigation = _cloneDeep(installationManager.forms.settings);
      sidebarData = { ...installationData };

      if (navigation && navigation.kioskAppVersion) {
        const kioskAppVersions = state
          .getIn(["data", "kioskAppVersions"], Map({}))
          .toJS();
        const osPlatform = installationData ? installationData.osPlatform : null;
        const os = osPlatform === "darwin" ? "mac" : "windows";
        const versionsForOS = kioskAppVersions[os] || [];

        const options = versionsForOS.map(function (entry) {
          return { label: entry.version, value: entry.version };
        });

        navigation.kioskAppVersion = {
          ...navigation.kioskAppVersion,
          type: "select",
          options
        };
      }
    }
  }
  const sidebarMode = `${state.getIn(["base", "mainSidebar", "mode"])}Extra`;

  return {
    username: user.get("email", null),
    isLoading,
    sidebarMode,
    installationID,
    installationType,
    section,
    userPreferencesSelector,
    userPreferences,
    sidebarProps: {
      title: sideBarTitle,
      navigation,
      structure,
      sidebarData,
      isFormDataDirty,
      section,
      slugBase: sideBarSlugBase,
      goBackAction
    },
    installationType,
    // iFrameKey: installationEditor.iFrameKey, // May need this if we're showing something in an iFrame, like the Project Editor.
    installationData,
    installationRevertCopy,
    allProjects,
    settings: state.get("base", Map).toJS()
  };
};
